import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    TextField,
    Typography,
    useTheme,
    Stack,
    Paper,
    IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { useItemValidator } from "../../hooks/useItemValidator";
import { fetchWithAuth } from "../../utils/api";

const AdminMarkRerelease = () => {
    const theme = useTheme();

    const [input, setInput] = useState("");
    const [markedItems, setMarkedItems] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [summarySuccess, setSummarySuccess] = useState([]);
    const [summaryFailed, setSummaryFailed] = useState([]);
    const { validateItemOnly, loading } = useItemValidator();

    const handleSearch = () => {
        if (loading) {
            setSnackbar({ open: true, message: "Item list is still loading..." });
            return;
        }

        const { validItems, errors } = validateItemOnly(input);

        if (errors.length > 0) {
            setSnackbar({ open: true, message: errors.join(", ") });
        }

        if (validItems.length > 0) {
            setMarkedItems((prev) => [
                ...prev,
                ...validItems.filter((item) => !prev.includes(item)),
            ]);
        }

        setInput("");
    };

    const handleRemove = (itemToRemove) => {
        setMarkedItems((prev) => prev.filter((item) => item !== itemToRemove));
    };

    const handleSubmit = () => {
        setConfirmOpen(true);
    };

    const confirmSubmit = async () => {
        setConfirmOpen(false);
        try {
            const response = await fetchWithAuth("/items/mark-rerelease", {
                method: "POST",
                body: {itemNames: markedItems},
            });

            if (response.success) {
                setSummarySuccess(response.updated || []);
                setSummaryFailed(response.failed || []);
                setSummaryOpen(true);
            } else {
                throw new Error("Unexpected response from server.");
            }
        } catch (err) {
            console.error("Failed to mark rerelease", err);
            setSnackbar({ open: true, message: "Error marking rerelease" });
        }
        setMarkedItems([]);
    };

    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: "bold",
                    color: theme.palette.accent.main,
                    textAlign: "center",
                    mb: 2,
                }}
            >
                admin: mark rerelease items
            </Typography>

            <Typography paragraph>
                Items selected will be set as volatile. All trades for the item will be marked as outdated. Value will be set to null. Remember to set notes or key events as needed!
            </Typography>

            <Box
                sx={{
                    maxWidth: "800px",
                    mx: "auto",
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Box
                    component="form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                    }}
                    display="flex"
                    gap={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems="center"
                >
                    <TextField
                        fullWidth
                        label="Enter item names (comma separated)"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        size="small"
                    />
                    <Button
                        variant="contained"
                        type="submit"
                        startIcon={<SearchIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        Search
                    </Button>
                </Box>

                {markedItems.length > 0 && (
                    <Box mt={4}>
                        <Typography variant="h6" gutterBottom>
                            Items to be marked
                        </Typography>
                        <Stack spacing={1}>
                            {markedItems.map((item) => (
                                <Paper
                                    key={item.toLowerCase()}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        backgroundColor: theme.palette.background.default,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography>{item}</Typography>
                                    <IconButton size="small" onClick={() => handleRemove(item)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Stack>

                        <Box mt={3} display="flex" justifyContent="space-between" gap={2}>
                            <Button variant="outlined" color="secondary" onClick={() => setMarkedItems([])} fullWidth>
                                Clear All
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
                                Submit
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Confirm Modal */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        are you sure you want to mark these items? <strong>it's a real pain to undo and i'll cry if u ping me to fix it</strong>
                    </Typography>
                    <ul>
                        {markedItems.map((item) => (
                            <li key={item}>
                                <Typography>{item}</Typography>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={confirmSubmit} variant="contained" color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Summary</DialogTitle>
                <DialogContent dividers>
                    {summarySuccess.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom color="success.main">
                                Marked Items
                            </Typography>
                            <ul>
                                {summarySuccess.map((item) => (
                                    <li key={`success-${item}`}>
                                        <Typography>{item}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {summaryFailed.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom color="error.main">
                                Failed to Mark
                            </Typography>
                            <ul>
                                {summaryFailed.map((item) => (
                                    <li key={`failed-${item}`}>
                                        <Typography>{item}</Typography>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSummaryOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ open: false, message: "" })}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box>
    );
};

export default AdminMarkRerelease;